import axios from 'axios';
import { Application, FeatureFlag, TargetingInfo, User } from './interface';
import config from '../../utilities/config';
import { logger } from '../workers/helpers';

const namespace = 'FeatureFlagService';

/**
 * Class representing the FeatureFlagService.
 */
class FeatureFlagService {
    /**
     * The base URL for the API.
     * @private
     */
    private static readonly API_BASE_URL = config.api.featureFlag; // Update with your actual API base URL

    /**
     * Record of all feature flags by application ID.
     * @private
     * @static
     */
    private static featureFlagsByApplicationId: Record<number, FeatureFlag[]> = {};

    /**
     * Map of application IDs to subscribers.
     * @private
     * @static
     */
    private static subscribers: Map<number, Subscriber[]> = new Map();

    /**
     * Method for returning the feature flags for a specific application.
     * @param {number} applicationId - The ID of the application for which to fetch feature flags.
     * @returns {FeatureFlag[]} An array of feature flags for the specified application.
     */
    public static getFeatureFlagsForApplication(applicationId: number): FeatureFlag[] {
        return FeatureFlagService.featureFlagsByApplicationId[applicationId] || [];
    }

    /**
     * Method for updating the feature flags for a specific application.
     * @param {number} applicationId - The ID of the application for which to update feature flags.
     * @param {FeatureFlag} featureFlags - The feature flags to update.
     * @returns {void}
     */
    public static setFeatureFlagsForApplication(applicationId: number, featureFlags: FeatureFlag[]): void {
        if (featureFlags.length > 0) {
            logger.debug(namespace, `Setting feature flags for applicationId: ${applicationId}`);
            featureFlags.forEach((featureFlag) => {
                logger.debug(namespace, `Feature flag: ${featureFlag.featureName}, isEnabled: ${featureFlag.isEnabled}`);
                if (featureFlag.targetingInfo) {
                    const targetingInfo = FeatureFlagService.parseFeatureFlags(featureFlag.targetingInfo);
                    featureFlag.userRoles = targetingInfo.userRoles;
                    featureFlag.userIds = targetingInfo.userIds;
                    featureFlag.minVersion = targetingInfo.minVersion;
                    featureFlag.maxVersion = targetingInfo.maxVersion;
                    featureFlag.environments = targetingInfo.environments;
                    featureFlag.platforms = targetingInfo.platforms;
                    featureFlag.devices = targetingInfo.devices;
                    featureFlag.startTime = targetingInfo.startTime;
                    featureFlag.endTime = targetingInfo.endTime;
                }
            });
        }
        FeatureFlagService.featureFlagsByApplicationId[applicationId] = featureFlags;
        FeatureFlagService.publish(applicationId, featureFlags);
    }

    /**
    * Method for subscribing to feature flag updates for a specific application.
    * @param {number} applicationId - The ID of the application for which to subscribe to feature flag updates.
    * @param {string} subscriberId - The ID of the subscriber.
    * @param {Function} callback - The callback function to call when a message is received.
    * @returns {void}
    */
    public static subscribe(applicationId: number, subscriberId: string, callback: (message?: FeatureFlag[]) => void): void {
        const subscribers = this.subscribers.get(applicationId);
        if (subscribers) {
            subscribers.push({ subscriberId, callback });
        } else {
            this.subscribers.set(applicationId, [{ subscriberId, callback }]);
        }
    }

    /**
     * Method for publishing a message to all subscribers of a specific application.
     * @param {number} applicationId - The ID of the application for which to publish a message.
     * @param {FeatureFlag[]} message - The message to publish.
     * @returns {void}
     */
    public static publish(applicationId: number, message?: FeatureFlag[]): void {
        const subscribers = this.subscribers.get(applicationId);
        if (subscribers) {
            subscribers.forEach((subscriber) => {
                subscriber.callback(message);
            });
        }
    }

    /**
     * Method for unsubscribing from feature flag updates for a specific application.
     * @param {number} applicationId - The ID of the application for which to unsubscribe from feature flag updates.
     * @param {string} subscriberId - The ID of the subscriber.
     * @returns {void}
     */
    public static unsubscribe(applicationId: number, subscriberId: string): void {
        const subscribers = this.subscribers.get(applicationId);
        if (subscribers) {
            const index = subscribers.findIndex((subscriber) => subscriber.subscriberId === subscriberId);
            if (index !== -1) {
                subscribers.splice(index, 1);
            }
        }
    }

    /**
* Method for checking if a feature flag is enabled for a specific application and optional parameters.
* @param {number} applicationId - The ID of the application for which to check the feature flag.
* @param {string} featureName - The name of the feature flag to check.
* @param {string} userId - The ID of the user for which to check the feature flag.
* @param {string[]} userRoles - The roles of the user for which to check the feature flag.
* @param {string} environment - The environment for which to check the feature flag.
* @param {string} platform - The platform for which to check the feature flag.
* @param {string} device - The device for which to check the feature flag.
* @param {string} version - The version for which to check the feature flag.
* @returns {boolean} True if the feature flag is enabled, false otherwise.
*/
    public static isFeatureEnabled({ applicationId, featureName, userId, userRoles, environment, platform, device, version }:
        {
            applicationId: number,
            featureName: string,
            userId?: string,
            userRoles?: string[],
            environment?: string,
            platform?: string,
            device?: string,
            version?: string,
        }
    ): boolean {
        logger.debug(namespace, `Checking feature flag '${featureName}' for applicationId: ${applicationId}`);

        const featureFlags = FeatureFlagService.getFeatureFlagsForApplication(applicationId);

        // Find the feature flag with the matching name
        const featureFlag = featureFlags.find((flag) => flag.featureName === featureName);
        logger.debug(namespace, `Feature flag found: ${featureFlag ? 'Yes' : 'No'}`);

        if (!featureFlag) {
            logger.debug(namespace, `Feature flag '${featureName}' not found.`);
            return false;
        }

        // Check if the feature flag is enabled
        logger.debug(namespace, `Feature flag '${featureName}' is enabled: ${featureFlag.isEnabled}`);
        if (!featureFlag.isEnabled) {
            logger.debug(namespace, `Feature flag '${featureName}' is disabled for the given parameters.`);
            return false;
        }

        // Check if the feature flag is targeted to the specified user
        if (userId && featureFlag.userIds) {
            logger.debug(namespace, `Checking userId '${userId}' against feature flag userIds: ${featureFlag.userIds}`);
            if (!featureFlag.userIds.includes(userId)) {
                logger.debug(namespace, `Feature flag '${featureName}' is disabled for the given user.`);
                return false;
            }
        }

        // Check if the feature flag is targeted to the specified user role
        if (userRoles && featureFlag.userRoles) {
            logger.debug(namespace, `Checking userRoles '${userRoles}' against feature flag userRoles: ${featureFlag.userRoles}`);
            if (!featureFlag.userRoles.some(role => userRoles.includes(role))) {
                logger.debug(namespace, `Feature flag '${featureName}' is disabled for the given user roles.`);
                return false;
            }
        }

        // Check if the feature flag is targeted to the specified environment
        if (environment && featureFlag.environments) {
            logger.debug(namespace, `Checking environment '${environment}' against feature flag environments: ${featureFlag.environments}`);
            if (!featureFlag.environments.includes(environment)) {
                logger.debug(namespace, `Feature flag '${featureName}' is disabled for the given environment.`);
                return false;
            }
        }

        // Check if the feature flag is targeted to the specified platform
        if (platform && featureFlag.platforms) {
            logger.debug(namespace, `Checking platform '${platform}' against feature flag platforms: ${featureFlag.platforms}`);
            if (!featureFlag.platforms.includes(platform)) {
                logger.debug(namespace, `Feature flag '${featureName}' is disabled for the given platform.`);
                return false;
            }
        }

        // Check if the feature flag is targeted to the specified device
        if (device && featureFlag.devices) {
            logger.debug(namespace, `Checking device '${device}' against feature flag devices: ${featureFlag.devices}`);
            if (!featureFlag.devices.includes(device)) {
                logger.debug(namespace, `Feature flag '${featureName}' is disabled for the given device.`);
                return false;
            }
        }

        // Check if the feature flag is targeted to the specified version, min and max version are inclusive but optional
        if (version) {
            const currentVersion = FeatureFlagService.getVersionNumber(version);
            const minVersion = featureFlag.minVersion ? FeatureFlagService.getVersionNumber(featureFlag.minVersion) : null;
            const maxVersion = featureFlag.maxVersion ? FeatureFlagService.getVersionNumber(featureFlag.maxVersion) : null;

            logger.debug(namespace, `Checking version '${version}' against minVersion: ${featureFlag.minVersion} and maxVersion: ${featureFlag.maxVersion}`);
            if ((minVersion && FeatureFlagService.compareVersions(currentVersion, minVersion) < 0) ||
                (maxVersion && FeatureFlagService.compareVersions(currentVersion, maxVersion) > 0)) {
                logger.debug(namespace, `Feature flag '${featureName}' is disabled for the given version.`);
                return false;
            }
        }

        // Check if the feature flag is targeted to the current time, start and end time are inclusive but optional
        const currentTime = new Date();
        const startTime = featureFlag.startTime ? new Date(featureFlag.startTime) : null;
        const endTime = featureFlag.endTime ? new Date(featureFlag.endTime) : null;

        logger.debug(namespace, `Checking current time against startTime: ${startTime} and endTime: ${endTime}`);
        if ((startTime && currentTime < startTime) || (endTime && currentTime > endTime)) {
            logger.debug(namespace, `Feature flag '${featureName}' is disabled for the given time.`);
            return false;
        }

        logger.debug(namespace, `Feature flag '${featureName}' is enabled for the given parameters.`);
        return true;
    }

    /**
     * Method for getting the version number from a version string.
     * @param {string} version - The version string from which to get the version number.
     * @returns {{ major: number, minor: number, patch: number }} The version number.
     */
    public static getVersionNumber(version: string): { major: number, minor: number, patch: number } {
        const versionParts = version.trim().split('.');
        const major = parseInt(versionParts[0], 10);
        const minor = parseInt(versionParts[1], 10) || 0;
        const patch = parseInt(versionParts[2], 10) || 0;
        return { major, minor, patch };
    }

    /**
     * Method for comparing two version numbers.
     * @param {{ major: number, minor: number, patch: number }} currentVersion - The current version number.
     * @param {{ major: number, minor: number, patch: number }} targetVersion - The target version number.
     * @returns {number} -1 if the current version is less than the target version, 0 if they are equal, 1 if the current version is greater than the target version.
     * @throws {Error} If the version numbers are invalid.
     * @public
     * @static
     */
    public static compareVersions(currentVersion: { major: number, minor: number, patch: number }, targetVersion: { major: number, minor: number, patch: number }): number {
        if (currentVersion.major < targetVersion.major) {
            return -1;
        } else if (currentVersion.major > targetVersion.major) {
            return 1;
        } else {
            if (currentVersion.minor < targetVersion.minor) {
                return -1;
            } else if (currentVersion.minor > targetVersion.minor) {
                return 1;
            } else {
                if (currentVersion.patch < targetVersion.patch) {
                    return -1;
                } else if (currentVersion.patch > targetVersion.patch) {
                    return 1;
                } else {
                    return 0;
                }
            }
        }
    }

    /**
     * Fetches all applications from the API.
     * @returns {Promise<Application[]>} A promise that resolves to an array of applications.
     * @throws {Error} If an error occurs while fetching applications.
     */
    public static async getAllApplications(): Promise<Application[]> {
        try {
            const response = await axios.get<Application[]>(`${FeatureFlagService.API_BASE_URL}/applications`);
            return response.data;
        } catch (error) {
            throw new Error('Error fetching applications');
        }
    }

    /**
     * Fetches an application by its ID from the API.
     * @param {number} applicationId - The ID of the application to fetch.
     * @returns {Promise<Application>} A promise that resolves to the fetched application.
     * @throws {Error} If an error occurs while fetching the application.
     */
    public static async getApplicationById(applicationId: number): Promise<Application> {
        try {
            const response = await axios.get<Application>(`${FeatureFlagService.API_BASE_URL}/applications/${applicationId}`);
            return response.data;
        } catch (error) {
            throw new Error('Error fetching application by ID');
        }
    }

    /**
     * Creates a new application with the given name.
     * @param {string} applicationName - The name of the new application.
     * @returns {Promise<Application>} A promise that resolves to the created application.
     * @throws {Error} If an error occurs while creating the application.
     */
    public static async createApplication(applicationName: string): Promise<Application> {
        try {
            const response = await axios.post<Application>(`${FeatureFlagService.API_BASE_URL}/applications`, {
                applicationName,
            });
            return response.data;
        } catch (error) {
            throw new Error('Error creating application');
        }
    }

    /**
     * Fetches all active users from the API.
     * @returns {Promise<User[]>} A promise that resolves to an array of active users.
     * @throws {Error} If an error occurs while fetching active users.
     */
    public static async getAllActiveUsers(): Promise<User[]> {
        try {
            const response = await axios.get<User[]>(`${FeatureFlagService.API_BASE_URL}/users/active`);
            return response.data;
        } catch (error) {
            throw new Error('Error fetching active users');
        }
    }

    /**
     * Method for parsing the feature flags from a JSON string.
     * @param {string} featureFlagsJson - The JSON string to parse.
     * @returns {FeatureFlag[]} An array of feature flags.
     * @throws {Error} If the JSON string is invalid.
     * @private
     * @static
     */
    private static parseFeatureFlags(featureFlagsJson: string): TargetingInfo {
        try {
            return JSON.parse(featureFlagsJson);
        } catch (error) {
            throw new Error('Error parsing feature flags');
        }
    }

    /**
     * Converts targeting info from a JSON string to a JSON object.
     * @param {FeatureFlag[]} featureFlags - The feature flags for which to convert targeting info.
     * @returns {FeatureFlag[]} The feature flags with converted targeting info.
     */
    private static convertTargetingInfo(featureFlags: FeatureFlag[]): FeatureFlag[] {
        featureFlags.forEach((featureFlag) => {
            if (featureFlag.targetingInfo) {
                const targetingInfo = FeatureFlagService.parseFeatureFlags(featureFlag.targetingInfo);
                featureFlag.userRoles = targetingInfo.userRoles;
                featureFlag.userIds = targetingInfo.userIds;
                featureFlag.minVersion = targetingInfo.minVersion;
                featureFlag.maxVersion = targetingInfo.maxVersion;
                featureFlag.environments = targetingInfo.environments;
                featureFlag.platforms = targetingInfo.platforms;
                featureFlag.devices = targetingInfo.devices;
                featureFlag.startTime = targetingInfo.startTime;
                featureFlag.endTime = targetingInfo.endTime;
            }
        });

        return featureFlags;
    }

    /**
     * Fetches all feature flags from the API.
     * @returns {Promise<FeatureFlag[]>} A promise that resolves to an array of feature flags.
     * @throws {Error} If an error occurs while fetching feature flags.
     */
    public static async getAllFeatureFlags(): Promise<FeatureFlag[]> {
        try {
            const response = await axios.get<FeatureFlag[]>(`${FeatureFlagService.API_BASE_URL}/featureflags`);

            // Convert targetingInfo from JSON string to JSON object
            return FeatureFlagService.convertTargetingInfo(response.data);
        } catch (error) {
            throw new Error('Error fetching feature flags');
        }
    }

    /**
     * Fetches a feature flag by its feature name from the API.
     * @param {string} featureName - The name of the feature flag to fetch.
     * @returns {Promise<FeatureFlag>} A promise that resolves to the fetched feature flag.
     * @throws {Error} If an error occurs while fetching the feature flag.
     */
    public static async getFeatureFlagByName(featureName: string): Promise<FeatureFlag[]> {
        try {
            const response = await axios.get<FeatureFlag[]>(`${FeatureFlagService.API_BASE_URL}/featureflags`);
            return FeatureFlagService.convertTargetingInfo(response.data.filter((featureFlag) => featureFlag.featureName === featureName));
        } catch (error) {
            throw new Error('Error fetching feature flag by name');
        }
    }

    /**
     * Fetches a feature flag by its ID from the API.
     * @param {number} featureId - The ID of the feature flag to fetch.
     * @returns {Promise<FeatureFlag>} A promise that resolves to the fetched feature flag.
     * @throws {Error} If an error occurs while fetching the feature flag.
     */
    public static async getFeatureFlagById(featureId: number): Promise<FeatureFlag> {
        try {
            const response = await axios.get<FeatureFlag>(`${FeatureFlagService.API_BASE_URL}/featureflags/${featureId}`);
            return FeatureFlagService.convertTargetingInfo([response.data])[0];
        } catch (error) {
            throw new Error('Error fetching feature flag by ID');
        }
    }

    /**
     * Creates a new feature flag with the provided data.
     * @param {CreateFeatureFlagDTO} dto - The data for creating the feature flag.
     * @returns {Promise<FeatureFlag>} A promise that resolves to the created feature flag.
     * @throws {Error} If an error occurs while creating the feature flag.
     */
    public static async createFeatureFlag(dto: CreateFeatureFlagDTO): Promise<FeatureFlag> {
        try {
            const response = await axios.post<FeatureFlag>(`${FeatureFlagService.API_BASE_URL}/featureflags`, dto);
            return response.data;
        } catch (error) {
            throw new Error('Error creating feature flag');
        }
    }

    /**
     * Updates a feature flag with the provided data.
     * @param {UpdateFeatureFlagDTO} dto - The data for updating the feature flag.
     * @returns {Promise<FeatureFlag>} A promise that resolves to the updated feature flag.
     * @throws {Error} If an error occurs while updating the feature flag.
     */
    public static async updateFeatureFlag(dto: UpdateFeatureFlagDTO): Promise<FeatureFlag> {
        try {
            const stringifiedTargetingInfo = JSON.stringify({
                userRoles: dto.userRoles,
                userIds: dto.userIds,
                minVersion: dto.minVersion,
                maxVersion: dto.maxVersion,
                environments: dto.environments,
                platforms: dto.platforms,
                devices: dto.devices,
                startTime: dto.startTime,
                endTime: dto.endTime,
            });

            dto.targetingInfo = stringifiedTargetingInfo;
            dto.userRoles = undefined;
            dto.userIds = undefined;
            dto.minVersion = undefined;
            dto.maxVersion = undefined;
            dto.environments = undefined;
            dto.platforms = undefined;
            dto.devices = undefined;
            dto.startTime = undefined;
            dto.endTime = undefined;

            const response = await axios.put<FeatureFlag>(`${FeatureFlagService.API_BASE_URL}/featureflags/${dto.featureId}`, dto);
            return response.data;
        } catch (error) {
            throw new Error('Error updating feature flag');
        }
    }

    /**
     * Deletes a feature flag by its ID.
     * @param {number} featureId - The ID of the feature flag to delete.
     * @throws {Error} If an error occurs while deleting the feature flag.
     */
    public static async deleteFeatureFlag(featureId: number): Promise<void> {
        try {
            await axios.delete(`${FeatureFlagService.API_BASE_URL}/featureflags/${featureId}`);
        } catch (error) {
            throw new Error('Error deleting feature flag');
        }
    }
}

export default FeatureFlagService;

/**
 * Data transfer object for creating a feature flag.
 * @interface
 */
export interface CreateFeatureFlagDTO {
    applicationId: number;
    featureName: string;
    isEnabled: boolean;
    description?: string;
    startTime?: string;
    endTime?: string;
    minVersion?: string;
    maxVersion?: string;
    userRoles?: string[];
    userIds?: string[];
    environments?: string[];
    platforms?: string[];
    devices?: string[];
    targetingInfo?: string;
}

/**
 * Data transfer object for updating a feature flag.
 * @interface
 */
interface UpdateFeatureFlagDTO extends CreateFeatureFlagDTO {
    featureId: number;
}

/**
 * Interface for a subscriber.
 * @interface
 * @property {string} subscriberId - The ID of the subscriber.
 * @property {Function} callback - The callback function to call when a message is received.
 */
interface Subscriber {
    subscriberId: string;
    callback: (message?: any) => void;
}